import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { CheckboxBase } from "@faire/slate/private/checkbox/CheckboxBase";
import {
  CheckboxChecked,
  ICheckboxPropsConstraints,
  ICheckboxWebProps,
} from "@faire/slate/private/checkbox/types";
import { buildFormIds } from "@faire/slate/private/form/utils";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

import { CHECKBOX_INPUT_UNIQUE_ID_PREFIX } from "./consts";

type IProps = {
  /**
   * Whether the checkbox is checked, "indeterminate" is used to denote partially checked.
   *
   * @default false
   */
  checked?: CheckboxChecked;
  /**
   * Whether the checkbox is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The checkbox label.
   */
  label?: IText;
  /**
   * The checkbox description.
   */
  description?: IText;
  /**
   * Whether to give the unchecked checkbox a solid fill so it stays legible on
   * non-white surfaces, like images.
   *
   * @default false
   */
  showFill?: boolean;
  // DO NOT ADD any new prop without aligning with Slate team first
} & ICheckboxWebProps;

/**
 * Checkboxes enable users to select one or more items from a list.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Checkbox-2242efb5c25a8022b488db5eb142c4a7 | Slate/Checkbox}
 */
export const Checkbox: React.FC<ICheckboxPropsConstraints<IProps>> = ({
  id: idProp,
  label,
  description,
  showFill = false,
  ...props
}) => {
  const { id, helperId } = buildFormIds({
    prefix: CHECKBOX_INPUT_UNIQUE_ID_PREFIX,
    idProp,
    label,
    helper: description,
  });
  return (
    <div
      className={checkboxStyle.cls({
        filled: showFill,
        className: componentCls(),
      })}
      data-component-name="Slate/Checkbox"
    >
      <CheckboxBase
        {...props}
        aria-describedby={helperId}
        hasLabel={!!label}
        id={id}
      />
      {label ? (
        <label
          className="fs-checkbox__label interactive"
          data-disabled={props.disabled}
          htmlFor={id}
        >
          {label}
        </label>
      ) : null}
      {description ? (
        <span
          className="fs-checkbox__description"
          data-disabled={props.disabled}
          id={helperId}
        >
          {description}
        </span>
      ) : null}
    </div>
  );
};

const checkboxStyle = tsv({
  base: "fs-checkbox",
  variants: {
    filled: { true: "fs-checkbox--filled" },
  },
});
